import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, DatePicker, Input, Table, Tag, Tabs, Select, Modal, Checkbox, Dropdown, Menu } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { ToastContainer, toast } from 'react-toastify';

import { BiExport } from 'react-icons/bi';
import { FaEye, FaPen, FaPlus, FaSearch, FaUserCheck, FaUserTimes } from 'react-icons/fa';
import { FaBoxArchive, FaBoxOpen } from "react-icons/fa6";
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';
import { MdOutlineManageAccounts, MdDoneAll, MdCancel } from 'react-icons/md';
import { BsThreeDotsVertical } from 'react-icons/bs';

import '../../../css/UserAdmin/Global.css';
import '../../../css/UserAdmin/SideNav.css';
import '../../../css/UserAdmin/AccountTable.css';
import 'react-toastify/dist/ReactToastify.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

dayjs.extend(utc);
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL;

const ManageAccountsPage = () => {
  const navigate = useNavigate();
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  const [activeTabKey, setActiveTabKey] = useState('all');
  const [allRawData, setAllRawData] = useState({ accounts: [], enrollees: [] });
  const [dataForCurrentTab, setDataForCurrentTab] = useState([]); 
  const [currentColumns, setCurrentColumns] = useState([]);
  const [processedTableData, setProcessedTableData] = useState([]);


  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFiltersFromAnt, setTableFiltersFromAnt] = useState({});
  const [sorterFromAnt, setSorterFromAnt] = useState({});
  const [showArchived, setShowArchived] = useState(false);

  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkActionModalVisible, setIsBulkActionModalVisible] = useState(false);
  const [currentBulkAction, setCurrentBulkAction] = useState(null);
  const [itemsForBulkAction, setItemsForBulkAction] = useState([]);
  const [exceptions, setExceptions] = useState(new Set());


  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    total: 0,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchAllData = async (currentShowArchived = showArchived) => {
    setLoading(true);
    try {
      const [accountsResponse, enrolleesResponse] = await Promise.all([
        fetch(`${API_URL}/api/admin/accounts?archived=${currentShowArchived}`),
        fetch(`${API_URL}/api/enrollee-applicants?archived=${currentShowArchived}`)
      ]);

      const processResponse = async (response, type) => {
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const textResponse = await response.text();
          console.error(`Expected JSON for ${type} but got ${contentType}. Status: ${response.status}. Response: ${textResponse}`);
          throw new Error(`Invalid response format for ${type}.`);
        }
        const result = await response.json();
        if (!response.ok) {
          console.error(`HTTP error for ${type}! status: ${response.status}`, result);
          throw new Error(result.message || `Failed to fetch ${type}.`);
        }
        return result;
      };

      const accountsResult = await processResponse(accountsResponse, 'accounts');
      const enrolleesResult = await processResponse(enrolleesResponse, 'enrollees');

      const transformedAccounts = (accountsResult.data || []).map(item => ({
        ...item, _id: item._id, key: `acc-${item._id || item.userID}`, displayID: item.userID,
        name: `${item.firstName} ${item.middleName || ''} ${item.lastName}`.trim(),
        roleForDisplay: item.role, hasCustomAccessForDisplay: item.hasCustomAccess, _dataType: 'account',
        academicYear: item.academicYear || null, createdAt: item.createdAt
      }));

      const enrolleeDataArray = Array.isArray(enrolleesResult) ? enrolleesResult : (enrolleesResult.data || []);
      const transformedEnrollees = enrolleeDataArray.map(item => ({
        ...item, _id: item._id, key: `enr-${item._id || item.applicantID}`, displayID: item.applicantID,
        name: `${item.firstName} ${item.middleName || ''} ${item.lastName}`.trim(),
        email: item.email, status: item.status, roleForDisplay: 'Enrollee Applicant',
        hasCustomAccessForDisplay: 'N/A', isArchived: item.isArchived || false, _dataType: 'enrollee',
        academicYear: item.academicYear || null, createdAt: item.createdAt
      }));
      
      setAllRawData({ accounts: transformedAccounts, enrollees: transformedEnrollees });

    } catch (error) {
      console.error('Error fetching all data:', error);
      toast.error(`Failed to fetch data: ${error.message || 'Unknown error'}`);
      setAllRawData({ accounts: [], enrollees: [] });
    }
  };
  
  useEffect(() => {
      const { accounts, enrollees } = allRawData;
      if (accounts.length === 0 && enrollees.length === 0 && !loading) {
          setAvailableYears([new Date().getFullYear()]);
          return;
      }
      const allItems = [...accounts, ...enrollees];
      let minYearInData = Infinity; let maxYearInData = -Infinity;
      if (allItems.length > 0) {
          const yearsFromData = allItems.map(item => item.createdAt && dayjs(item.createdAt).isValid() ? dayjs(item.createdAt).year() : null).filter(year => year !== null);
          if (yearsFromData.length > 0) { minYearInData = Math.min(...yearsFromData); maxYearInData = Math.max(...yearsFromData); }
      }
      const currentActualYear = new Date().getFullYear();
      const finalMinYear = Math.min(minYearInData, currentActualYear);
      const finalMaxYear = Math.max(maxYearInData, currentActualYear);
      const yearsArray = [];
      if (isFinite(finalMinYear) && isFinite(finalMaxYear)) { for (let y = finalMaxYear; y >= finalMinYear; y--) yearsArray.push(y); } 
      else { yearsArray.push(currentActualYear); }
      setAvailableYears(yearsArray);
  }, [allRawData, loading]);


  useEffect(() => { fetchAllData(showArchived); }, [showArchived]);

  useEffect(() => {
    setLoading(true);
    const { accounts, enrollees } = allRawData;
    let dataToProcess = [...accounts, ...enrollees];
    let columnsForCurrentTab = [];

    if (selectedYear && selectedYear !== 'all' && selectedYear !== null) {
        const yearToFilter = parseInt(selectedYear, 10);
        dataToProcess = dataToProcess.filter(item => item.createdAt && dayjs(item.createdAt).isValid() ? dayjs(item.createdAt).year() === yearToFilter : false);
    }

    switch (activeTabKey) {
      case 'enrollees': dataToProcess = dataToProcess.filter(item => item._dataType === 'enrollee'); columnsForCurrentTab = getEnrolleeColumns(); break;
      case 'students': dataToProcess = dataToProcess.filter(item => item._dataType === 'account' && item.role === 'Student'); columnsForCurrentTab = getAccountColumns('Student'); break;
      case 'faculty': dataToProcess = dataToProcess.filter(item => item._dataType === 'account' && item.role === 'Faculty'); columnsForCurrentTab = getAccountColumns('Faculty'); break;
      case 'admissions_staff': dataToProcess = dataToProcess.filter(item => item._dataType === 'account' && item.role === 'Admissions (Staff)'); columnsForCurrentTab = getAccountColumns('Admissions (Staff)'); break;
      case 'registrar_staff': dataToProcess = dataToProcess.filter(item => item._dataType === 'account' && item.role === 'Registrar (Staff)'); columnsForCurrentTab = getAccountColumns('Registrar (Staff)'); break;
      case 'accounting_staff': dataToProcess = dataToProcess.filter(item => item._dataType === 'account' && item.role === 'Accounting (Staff)'); columnsForCurrentTab = getAccountColumns('Accounting (Staff)'); break;
      case 'admin_sub': dataToProcess = dataToProcess.filter(item => item._dataType === 'account' && item.role === 'Administration (Sub-Admin)'); columnsForCurrentTab = getAccountColumns('Administration (Sub-Admin)'); break;
      case 'it_super_admin': dataToProcess = dataToProcess.filter(item => item._dataType === 'account' && item.role === 'IT (Super Admin)'); columnsForCurrentTab = getAccountColumns('IT (Super Admin)'); break;
      default: columnsForCurrentTab = getMixedColumns(); break;
    }
    setCurrentColumns(columnsForCurrentTab);

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      dataToProcess = dataToProcess.filter(item =>
        (item.name || '').toLowerCase().includes(searchLower) ||
        (item.displayID || '').toLowerCase().includes(searchLower) ||
        (item.email || '').toLowerCase().includes(searchLower) ||
        (item.roleForDisplay || '').toLowerCase().includes(searchLower)
      );
    }

    Object.keys(tableFiltersFromAnt).forEach(filterKey => {
      const filterValues = tableFiltersFromAnt[filterKey];
      if (filterValues && filterValues.length > 0) {
        const columnDef = columnsForCurrentTab.find(col => col.key === filterKey || col.dataIndex === filterKey);
        if (columnDef && typeof columnDef.onFilter === 'function') {
          dataToProcess = dataToProcess.filter(record => filterValues.some(value => columnDef.onFilter(value, record)));
        }
      }
    });
    
    if (sorterFromAnt.columnKey && sorterFromAnt.order) {
      const { columnKey, order, field } = sorterFromAnt;
      const columnDef = columnsForCurrentTab.find(col => col.key === columnKey || col.dataIndex === field);
      if (columnDef) {
        const sorterFn = columnDef.sorter; const dataIndex = columnDef.dataIndex;
        if (typeof sorterFn === 'function') {
          dataToProcess.sort((a, b) => { const result = sorterFn(a, b); return order === 'descend' ? -result : result; });
        } else if (sorterFn === true && dataIndex) {
          dataToProcess.sort((a, b) => {
            const valA = a[dataIndex]; const valB = b[dataIndex]; let comparison = 0;
            if (valA === null || valA === undefined) comparison = (order === 'ascend' ? -1 : 1);
            else if (valB === null || valB === undefined) comparison = (order === 'ascend' ? 1 : -1);
            else if (typeof valA === 'string' && typeof valB === 'string') comparison = valA.localeCompare(valB);
            else { if (valA < valB) comparison = -1; else if (valA > valB) comparison = 1; }
            return order === 'descend' ? comparison * -1 : comparison;
          });
        }
      }
    }
    setPagination(prev => ({ ...prev, total: dataToProcess.length }));
    setDataForCurrentTab(dataToProcess);
    setLoading(false);
  }, [allRawData, activeTabKey, searchTerm, tableFiltersFromAnt, sorterFromAnt, selectedYear]);

  useEffect(() => {
    if (dataForCurrentTab.length > 0 || pagination.total === 0) {
      const { current, pageSize } = pagination;
      const startIndex = (current - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setProcessedTableData(dataForCurrentTab.slice(startIndex, endIndex));
    } else if (dataForCurrentTab.length === 0 && pagination.total > 0) {
      setProcessedTableData([]);
    }
  }, [dataForCurrentTab, pagination]);


  const handleSearch = (value) => setSearchTerm(value);
  const handleClearFilters = () => {
    setTableFiltersFromAnt({}); setSorterFromAnt({}); setSearchTerm(''); setSelectedYear(null);
    setPagination(prev => ({ ...prev, current: 1 }));
  };
  const handleRefresh = () => {
    setSearchTerm(''); setTableFiltersFromAnt({}); setSorterFromAnt({}); setSelectedYear(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchAllData(showArchived);
  };
  const handleExport = () => { /* ... as before ... */
    const currentDate = new Date().toISOString().split('T')[0];
    const fileName = `accounts-report-${currentDate}.pdf`;
    const adminFullName = localStorage.getItem('fullName');
    const adminRole = localStorage.getItem('role');
    const adminUserID = localStorage.getItem('userID');

    fetch(`${API_URL}/api/admin/export/accounts?userID=${encodeURIComponent(adminUserID)}&fullName=${encodeURIComponent(adminFullName)}&role=${encodeURIComponent(adminRole)}`, {
      method: 'GET',
    })
    .then(response => {
      if (!response.ok) throw new Error(`Export failed with status: ${response.status}`);
      return response.blob();
    })
    .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        const logData = { userID: adminUserID, accountName: adminFullName, role: adminRole, action: 'Export', detail: `Exported accounts report: ${fileName}` };
        fetch(`${API_URL}/api/admin/system-logs`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', }, body: JSON.stringify(logData)
        }).catch(err => console.error('Log export error:', err));
      })
      .catch(error => {
        console.error('Export failed:', error);
        toast.error(`Export failed: ${error.message}`);
      });
  };
  const handleCreate = () => navigate('/admin/manage-accounts/create');
  const handleAccessControl = () => navigate('/admin/access-control');
  const handleAccountStatusToggle = async (record) => {
    const updatedStatus = record.status === 'Active' ? 'Inactive' : 'Active';
    try {
      const response = await fetch(`${API_URL}/api/admin/accounts/${record._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: updatedStatus }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to update account status');
      }
      toast.success('Account status updated!');
      const adminFullName = localStorage.getItem('fullName');
      const adminRole = localStorage.getItem('role');
      const adminUserID = localStorage.getItem('userID');
      const logAction = 'Update';
      const logDetail = `${logAction}: [${record.displayID || 'N/A'}] ${record.name || 'N/A'} (Role: ${record.roleForDisplay || 'N/A'})`;
      const logData = { userID: adminUserID, accountName: adminFullName, role: adminRole, action: logAction, detail: logDetail, };
      fetch(`${API_URL}/api/admin/system-logs`, { method: 'POST', headers: { 'Content-Type': 'application/json', }, body: JSON.stringify(logData)
      }).catch(err => console.error('Failed to log account status change:', err));
      fetchAllData(showArchived);
    } catch (error) {
      console.error('Error toggling account status:', error);
      toast.error(error.message);
    }
};
  const handleAccountArchiveToggle = async (record) => {
    const { _id, isArchived, status } = record;
    if (!isArchived && status === 'Active') {
      toast.warning('Cannot archive an active account. Please deactivate first.');
      return;
    }
    const actionType = isArchived ? 'Unarchive' : 'Archive';
    try {
      const response = await fetch(`${API_URL}/api/admin/archive/${_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', },
        body: JSON.stringify({ isArchived: !isArchived })
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to ${actionType.toLowerCase()} account`);
      }
      toast.success(`Account ${actionType.toLowerCase()}d successfully!`);
      const adminID = localStorage.getItem('userID');
      const adminName = localStorage.getItem('fullName');
      const adminRole = localStorage.getItem('role');
      const logDetail = `${actionType}d account [UserID: ${record.displayID || 'N/A'}] of ${record.name} (Role: ${record.roleForDisplay || 'N/A'})`;
      const logData = { userID: adminID, accountName: adminName, role: adminRole, action: actionType, detail: logDetail };
      await fetch(`${API_URL}/api/admin/system-logs`, { method: 'POST', headers: { 'Content-Type': 'application/json', }, body: JSON.stringify(logData) });
      fetchAllData(showArchived);
    } catch (error) {
      console.error(`${actionType} error:`, error);
      toast.error(error.message);
    }
};
  const handleEnrolleeStatusToggle = async (record) => {
    const updatedStatus = record.status === 'Active' ? 'Inactive' : 'Active';
    try {
      const response = await fetch(`${API_URL}/api/enrollee-applicants/${record._id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: updatedStatus }),
      });
      if (!response.ok) {
         const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to update enrollee status');
      }
      toast.success('Enrollee Applicant status updated!');
      const adminID = localStorage.getItem('userID');
      const adminName = localStorage.getItem('fullName');
      const adminRole = localStorage.getItem('role');
      const logAction = 'Update';
      const logDetail = `${logAction}: [ApplicantID: ${record.displayID || 'N/A'}] ${record.name || 'N/A'}`;
      const logData = { userID: adminID, accountName: adminName, role: adminRole, action: logAction, detail: logDetail };
      await fetch(`${API_URL}/api/admin/system-logs`, { method: 'POST', headers: { 'Content-Type': 'application/json', }, body: JSON.stringify(logData) });
      fetchAllData(showArchived);
    } catch (error) {
      console.error('Error toggling enrollee status:', error);
      toast.error(error.message);
    }
};
  const handleEnrolleeArchiveToggle = async (record) => {
    if (!record.hasOwnProperty('isArchived')) {
        toast.error("Archiving not supported for Enrollee Applicants in current setup.");
        return;
    }
    const { _id, isArchived, status } = record;
    if (!isArchived && status === 'Active') {
        toast.warning('Cannot archive an active enrollee. Please deactivate first.');
        return;
    }
    const actionType = isArchived ? 'Unarchive' : 'Archive';
    try {
      const response = await fetch(`${API_URL}/api/enrollee-applicants/${_id}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', },
        body: JSON.stringify({ isArchived: !isArchived })
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message ||`Failed to ${actionType.toLowerCase()} enrollee`);
      }
      toast.success(`Enrollee Applicant ${actionType.toLowerCase()}d successfully!`);
      const adminID = localStorage.getItem('userID');
      const adminName = localStorage.getItem('fullName');
      const adminRole = localStorage.getItem('role');
      const logDetail = `${actionType}d Enrollee Applicant [ApplicantID: ${record.displayID || 'N/A'}] of ${record.name}`;
      const logData = { userID: adminID, accountName: adminName, role: adminRole, action: actionType, detail: logDetail };
      await fetch(`${API_URL}/api/admin/system-logs`, { method: 'POST', headers: { 'Content-Type': 'application/json', }, body: JSON.stringify(logData) });
      fetchAllData(showArchived);
    } catch (error) {
      console.error(`${actionType} enrollee error:`, error);
      toast.error(error.message);
    }
};

const handleBulkAction = (actionType, allSelectedItemsFromTable) => {
    if (!allSelectedItemsFromTable || allSelectedItemsFromTable.length === 0) {
      toast.info('No items were selected for bulk action.');
      return;
    }

    let eligibleItems = [];
    let warningMessages = [];

    switch (actionType) {
        case 'activate':
            eligibleItems = allSelectedItemsFromTable.filter(item => {
                if (item.status === 'Active') return false;
                if (item.status === 'Pending Verification') { warningMessages.push(`${item.name} (${item.displayID}) is Pending Verification.`); return false; }
                if (item.isArchived) { warningMessages.push(`${item.name} (${item.displayID}) is Archived; unarchive first.`); return false; }
                return true;
            });
            break;
        case 'deactivate':
            eligibleItems = allSelectedItemsFromTable.filter(item => {
                if (item.status === 'Inactive') return false;
                if (item.status === 'Pending Verification') { warningMessages.push(`${item.name} (${item.displayID}) is Pending Verification.`); return false; }
                return true;
            });
            break;
        case 'archive':
            eligibleItems = allSelectedItemsFromTable.filter(item => {
                if (item.isArchived) return false;
                if (item.status === 'Active') { warningMessages.push(`${item.name} (${item.displayID}) is Active; deactivate first.`); return false; }
                if (item.status === 'Pending Verification' && item._dataType === 'account') { warningMessages.push(`Account ${item.name} (${item.displayID}) is Pending; cannot archive.`); return false; }
                if (item._dataType === 'enrollee' && !item.hasOwnProperty('isArchived')) { warningMessages.push(`Archiving not configured for enrollee: ${item.name}.`); return false; }
                return true;
            });
            break;
        case 'unarchive':
            eligibleItems = allSelectedItemsFromTable.filter(item => {
                if (!item.isArchived) return false;
                if (item._dataType === 'enrollee' && !item.hasOwnProperty('isArchived')) { warningMessages.push(`Unarchiving not configured for enrollee: ${item.name}.`); return false; }
                return true;
            });
            break;
        default: toast.error('Unknown bulk action.'); return;
    }

    if (warningMessages.length > 0) {
        toast.warning(<div><p>Some selected items are not eligible:</p><ul style={{ maxHeight: '100px', overflowY: 'auto', paddingLeft: '20px' }}>{warningMessages.map((msg, idx) => <li key={idx}>{msg}</li>)}</ul></div>, 7);
    }
    if (eligibleItems.length === 0) { toast.info('No items are eligible for the selected bulk action.'); return; }

    setItemsForBulkAction(eligibleItems);
    setCurrentBulkAction(actionType);
    setExceptions(new Set());
    setIsBulkActionModalVisible(true);
};

const confirmBulkAction = async () => {
    if (!currentBulkAction || itemsForBulkAction.length === 0) return;
    const finalItemsToProcess = itemsForBulkAction.filter(item => !exceptions.has(item._id));
    if (finalItemsToProcess.length === 0) { toast.info('No items selected in modal.'); setIsBulkActionModalVisible(false); return; }

    const accountsToProcess = finalItemsToProcess.filter(item => item._dataType === 'account').map(item => item._id);
    const enrolleesToProcess = finalItemsToProcess.filter(item => item._dataType === 'enrollee').map(item => item._id);

    setLoading(true); setIsBulkActionModalVisible(false);

    // Define the robust response handler here
    const handleApiResponse = async (response, entityType) => {
        const defaultErrorMsg = `${entityType.charAt(0).toUpperCase() + entityType.slice(1)}: An error occurred during bulk ${currentBulkAction}.`;
        try {
            if (!response.ok) {
                let errorDetail = `Status: ${response.status}`;
                try {
                    // Try to get more info from the body, even for errors
                    const errorData = await response.json();
                    errorDetail = errorData.message || errorData.error || errorDetail;
                } catch (e) {
                    // If body isn't JSON or empty, use status
                }
                console.error(`Bulk API error for ${entityType}: ${errorDetail}`);
                return { success: false, message: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)}: ${errorDetail}`, processedCount: 0 };
            }

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const textResponse = await response.text();
                const nonJsonMsg = `${entityType.charAt(0).toUpperCase() + entityType.slice(1)}: Invalid response format. Expected JSON but got ${contentType}. Response: ${textResponse.substring(0,100)}`;
                console.error(nonJsonMsg);
                return { success: false, message: nonJsonMsg, processedCount: 0 };
            }

            const result = await response.json();

            // Ensure the result is an object and has expected properties
            if (typeof result !== 'object' || result === null) {
                console.error(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)}: API returned JSON, but not an object. Received:`, result);
                return { success: false, message: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)}: Unexpected data structure from API.`, processedCount: 0 };
            }

            return {
                success: result.success !== undefined ? result.success : false,
                message: result.message || (result.success ? `${entityType.charAt(0).toUpperCase() + entityType.slice(1)}: Processed.` : defaultErrorMsg),
                processedCount: result.processedCount || 0
            };
        } catch (error) { // Catch errors from .json() parsing or other issues
            console.error(`Error processing API response for ${entityType}:`, error);
            return { success: false, message: `${defaultErrorMsg} (Client-side error: ${error.message})`, processedCount: 0 };
        }
    };

    try {
      let accountP = Promise.resolve({ success: true, message: "Accounts: None to process.", processedCount: 0 });
      let enrolleeP = Promise.resolve({ success: true, message: "Enrollees: None to process.", processedCount: 0 });

      if (accountsToProcess.length > 0) {
        accountP = fetch(`${API_URL}/api/admin/accounts/bulk-${currentBulkAction}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: accountsToProcess }),
        }).then(res => handleApiResponse(res, 'accounts')); // Use the handler
      }

      if (enrolleesToProcess.length > 0) {
        const canEnrolleeBeArchived = currentBulkAction === 'archive' || currentBulkAction === 'unarchive' ?
            finalItemsToProcess.some(item => item._dataType === 'enrollee' && item.hasOwnProperty('isArchived')) : true;

        if (!canEnrolleeBeArchived && (currentBulkAction === 'archive' || currentBulkAction === 'unarchive')) {
            enrolleeP = Promise.resolve({ success: true, message: "Enrollees: Archive/Unarchive not supported.", processedCount: 0 });
        } else {
            enrolleeP = fetch(`${API_URL}/api/enrollee-applicants/bulk-${currentBulkAction}`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: enrolleesToProcess }),
            }).then(res => handleApiResponse(res, 'enrollees')); // Use the handler
        }
      }

      const [accountResult, enrolleeResult] = await Promise.all([accountP, enrolleeP]);

      console.log("Account Result:", accountResult); // Should now always be an object
      console.log("Enrollee Result:", enrolleeResult); // Should now always be an object

      let summary = [];
      // Now these checks are safer because accountResult and enrolleeResult are guaranteed to be objects
      if (accountsToProcess.length > 0) {
          summary.push(accountResult.message || (accountResult.success ? `Accounts: ${accountResult.processedCount} OK.` : "Accounts: Failed."));
      }
      if (enrolleesToProcess.length > 0) {
          summary.push(enrolleeResult.message || (enrolleeResult.success ? `Enrollees: ${enrolleeResult.processedCount} OK.` : "Enrollees: Failed."));
      }

      const overallSuccess = (!accountsToProcess.length || accountResult.success) &&
                             (!enrolleesToProcess.length || enrolleeResult.success);

      if (summary.length === 0) {
          toast.info(`Bulk ${currentBulkAction}: No items were targeted by this action.`);
      } else if (overallSuccess) {
          toast.success(`Bulk ${currentBulkAction} successful: ${summary.join(' ')}`);
      } else {
          toast.warning(`Bulk ${currentBulkAction} issues: ${summary.join(' ')}`);
      }

      const adminID = localStorage.getItem('userID'), adminName = localStorage.getItem('fullName'), adminRole = localStorage.getItem('role');
      // Ensure processedCount is a number for logging
      const accProcessed = (accountResult && typeof accountResult.processedCount === 'number') ? accountResult.processedCount : 0;
      const enrProcessed = (enrolleeResult && typeof enrolleeResult.processedCount === 'number') ? enrolleeResult.processedCount : 0;
      const logDetail = `Bulk ${currentBulkAction}: ${finalItemsToProcess.length} confirmed.`;
      await fetch(`${API_URL}/api/admin/system-logs`, { method: 'POST', headers: { 'Content-Type': 'application/json', }, body: JSON.stringify({ userID: adminID, accountName: adminName, role: adminRole, action: `Update`, detail: logDetail }) });

    } catch (error) { // This outer catch is for errors in Promise.all or setting up promises
      console.error("General error in confirmBulkAction:", error);
      toast.error(`Bulk ${currentBulkAction} request error: ${error.message}`);
    } finally {
      setLoading(false); setSelectedRowKeys([]); fetchAllData(showArchived);
    }
};

  const ACTION_COLUMN_WIDTH = 200;

  const getBaseColumns = () => [
    { title: 'ID', dataIndex: 'displayID', key: 'displayID', width: 150, fixed: 'left', sorter: true, sortOrder: sorterFromAnt.columnKey === 'displayID' ? sorterFromAnt.order : null, ellipsis: true,},
    { title: 'Full Name', dataIndex: 'name', key: 'name', width: 200, fixed: 'left', sorter: true, sortOrder: sorterFromAnt.columnKey === 'name' ? sorterFromAnt.order : null, ellipsis: true,},
    { title: 'Email', dataIndex: 'email', key: 'email', width: 300, sorter: true, sortOrder: sorterFromAnt.columnKey === 'email' ? sorterFromAnt.order : null, ellipsis: true,},
    {
      title: 'Account Status', dataIndex: 'status', key: 'status', width: 170,
      filters: [{ text: 'Active', value: 'Active' }, { text: 'Inactive', value: 'Inactive' }, { text: 'Pending Verification', value: 'Pending Verification' }],
      onFilter: (value, record) => (record.status || '').includes(value),
      filteredValue: tableFiltersFromAnt.status || null,
      render: (status) => {
        let color;
        if (status === 'Active') color = 'green'; else if (status === 'Pending Verification') color = 'blue'; else color = 'volcano';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', width: 180, render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm') : 'N/A', sorter: true, sortOrder: sorterFromAnt.columnKey === 'createdAt' ? sorterFromAnt.order : null, filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters: localClearFilters }) => (<DateRangeFilter selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys} confirm={confirm} clearFilters={localClearFilters} />), onFilter: (value, record) => isInDateRange(value, record.createdAt), filteredValue: tableFiltersFromAnt.createdAt || null, },
    { title: 'Updated At', dataIndex: 'updatedAt', key: 'updatedAt', width: 180, render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm') : 'N/A', sorter: true, sortOrder: sorterFromAnt.columnKey === 'updatedAt' ? sorterFromAnt.order : null, filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters: localClearFilters }) => (<DateRangeFilter selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys} confirm={confirm} clearFilters={localClearFilters} />), onFilter: (value, record) => isInDateRange(value, record.updatedAt), filteredValue: tableFiltersFromAnt.updatedAt || null, },
  ];

  const getAccountActionColumn = () => ({
    title: 'Action', key: 'action', fixed: 'right', width: ACTION_COLUMN_WIDTH,
    render: (_, record) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Button style={{width:'100%'}} icon={record.isArchived ? <FaEye /> : <FaPen />} onClick={() => navigate(`/admin/manage-accounts/edit/${record._id}`)}>
          {record.isArchived ? 'View' : 'Edit'}
        </Button>
        {record.status !== 'Pending Verification' && (
          <Button style={{width:'100%'}} icon={record.status === 'Active' ? <FaUserTimes /> : <FaUserCheck />} danger={record.status === 'Active'} onClick={() => handleAccountStatusToggle(record)} disabled={record.status !== 'Active' && record.isArchived}>
            {record.status === 'Active' ? 'Deactivate' : 'Activate'}
          </Button>
        )}
        <Button style={{width:'100%'}} icon={record.isArchived ? <FaBoxOpen /> : <FaBoxArchive />} onClick={() => handleAccountArchiveToggle(record)} disabled={(!record.isArchived && record.status === 'Active') || record.status === 'Pending Verification'}>
          {record.isArchived ? 'Unarchive' : 'Archive'}
        </Button>
      </div>
    ),
});
  const getEnrolleeActionColumn = () => ({
    title: 'Action', key: 'action', fixed: 'right', width: ACTION_COLUMN_WIDTH,
    render: (_, record) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Button style={{width:'100%'}} icon={<FaPen />} onClick={() => navigate(`/admin/enrollee-applicant/edit/${record._id}`)}> 
          Edit Applicant
        </Button>
        {record.status !== 'Pending Verification' && (
          <Button style={{width:'100%'}} icon={record.status === 'Active' ? <FaUserTimes /> : <FaUserCheck />} danger={record.status === 'Active'} onClick={() => handleEnrolleeStatusToggle(record)} disabled={record.status !== 'Active' && record.isArchived}>
            {record.status === 'Active' ? 'Deactivate' : 'Activate'}
          </Button>
        )}
        {record.hasOwnProperty('isArchived') && (
          <Button style={{width:'100%'}} icon={record.isArchived ? <FaBoxOpen /> : <FaBoxArchive />} onClick={() => handleEnrolleeArchiveToggle(record)} disabled={(!record.isArchived && record.status === 'Active') || record.status === 'Pending Verification'}>
            {record.isArchived ? 'Unarchive' : 'Archive'}
          </Button>
        )}
      </div>
    ),
});

  const getAccountColumns = () => [
    ...getBaseColumns().filter(col => !['updatedAt', 'createdAt', 'email'].includes(col.key)),
    { title: 'Role', dataIndex: 'roleForDisplay', key: 'roleForDisplay', width: 180, render: (role) => <Tag>{role}</Tag>, sorter: true, sortOrder: sorterFromAnt.columnKey === 'roleForDisplay' ? sorterFromAnt.order : null, },
    { title: 'Custom Access', dataIndex: 'hasCustomAccessForDisplay', key: 'hasCustomAccessForDisplay', width: 140, render: (value) => value ? <Tag color="cyan">Yes</Tag> : <Tag color="orange">No</Tag>, filters: [{text: 'Yes', value: true}, {text: 'No', value: false}], onFilter: (value, record) => record.hasCustomAccessForDisplay === value, filteredValue: tableFiltersFromAnt.hasCustomAccessForDisplay || null },
    getAccountActionColumn(),
  ];

  const getEnrolleeColumns = () => [
    ...getBaseColumns().filter(col => !['updatedAt', 'createdAt'].includes(col.key)),
    { title: 'Role', dataIndex: 'roleForDisplay', key: 'roleForDisplay', width: 150, render: (role) => <Tag color="purple">{role}</Tag>, sorter: true, sortOrder: sorterFromAnt.columnKey === 'roleForDisplay' ? sorterFromAnt.order : null,},
    { title: 'Application Status', dataIndex: 'registrationStatus', key: 'registrationStatus', width: 150, render: (text) => text ? <Tag color="blue">{text}</Tag> : 'N/A', sorter: true, sortOrder: sorterFromAnt.columnKey === 'registrationStatus' ? sorterFromAnt.order : null, },
    { title: 'Academic Level (For Enrollment)', dataIndex: 'academicLevel', key: 'academicLevel', width: 180, render: (text) => text || 'N/A', sorter: true, sortOrder: sorterFromAnt.columnKey === 'academicLevel' ? sorterFromAnt.order : null, },
    { title: 'Academic Year (For Enrollment)', dataIndex: 'academicYear', key: 'academicYear', width: 180, render: (text) => text || 'N/A', sorter: true, sortOrder: sorterFromAnt.columnKey === 'academicYear' ? sorterFromAnt.order : null, },
    getEnrolleeActionColumn(),
  ];

  const getMixedColumns = () => [
    ...getBaseColumns(),
    {
      title: 'Role', dataIndex: 'roleForDisplay', key: 'roleForDisplay', width: 180,
      filters: [
        { text: 'Enrollee Applicant', value: 'Enrollee Applicant' }, { text: 'Student', value: 'Student' },
        { text: 'Faculty', value: 'Faculty' }, { text: 'Admissions (Staff)', value: 'Admissions (Staff)' },
        { text: 'Registrar (Staff)', value: 'Registrar (Staff)' }, { text: 'Accounting (Staff)', value: 'Accounting (Staff)' },
        { text: 'Administration (Sub-Admin)', value: 'Administration (Sub-Admin)' }, { text: 'IT (Super Admin)', value: 'IT (Super Admin)' },
      ],
      onFilter: (value, record) => (record.roleForDisplay || '').includes(value),
      filteredValue: tableFiltersFromAnt.roleForDisplay || null,
      sorter: true,
      sortOrder: sorterFromAnt.columnKey === 'roleForDisplay' ? sorterFromAnt.order : null,
      render: (role, record) => <Tag color={record._dataType === 'enrollee' ? 'purple' : undefined}>{role}</Tag>
    },
    {
      title: 'Custom Access', dataIndex: 'hasCustomAccessForDisplay', key: 'hasCustomAccessForDisplay', width: 150,
      filters: [ { text: 'Yes', value: true }, { text: 'No', value: false }, { text: 'N/A', value: 'N/A' } ],
      onFilter: (value, record) => record.hasCustomAccessForDisplay === value,
      filteredValue: tableFiltersFromAnt.hasCustomAccessForDisplay || null,
      sorter: true,
      sortOrder: sorterFromAnt.columnKey === 'hasCustomAccessForDisplay' ? sorterFromAnt.order : null,
      render: (value) => value === true ? <Tag color="cyan">Yes</Tag> : value === false ? <Tag color="orange">No</Tag> : <Tag>N/A</Tag>
    },
    {
      title: 'Academic Year (Enrollment)', dataIndex: 'academicYear', key: 'academicYear', width: 180,
      render: (text) => text || 'N/A', sorter: true,
      sortOrder: sorterFromAnt.columnKey === 'academicYear' ? sorterFromAnt.order : null,
    },
    {
      title: 'Action', key: 'action', fixed: 'right', width: ACTION_COLUMN_WIDTH,
      render: (_, record) => {
        if (record._dataType === 'account') return getAccountActionColumn().render(_, record);
        if (record._dataType === 'enrollee') return getEnrolleeActionColumn().render(_, record);
        return null;
      }
    }
  ];

  const tabItems = [
    { key: 'all', label: 'All Records' }, { key: 'enrollees', label: 'Enrollee Applicants' },
    { key: 'students', label: 'Students' }, { key: 'faculty', label: 'Faculty' },
    { key: 'admissions_staff', label: 'Admissions Staff'}, { key: 'registrar_staff', label: 'Registrar Staff'},
    { key: 'accounting_staff', label: 'Accounting Staff'}, { key: 'admin_sub', label: 'Sub-Admins'},
    { key: 'it_super_admin', label: 'Super Admins'},
  ];

  const DateRangeFilter = ({ selectedKeys, setSelectedKeys, confirm, clearFilters: localClearFilters }) => (
    <div style={{ padding: 8 }}>
      <RangePicker
        style={{ width: 250 }}
        value={selectedKeys && selectedKeys[0] ? [dayjs(selectedKeys[0].split('|')[0]), dayjs(selectedKeys[0].split('|')[1])] : []}
        onChange={(dates) => {
          setSelectedKeys(dates ? [`${dates[0].toISOString()}|${dates[1].toISOString()}`] : []);
        }}
      />
      <div style={{ marginTop: 8, textAlign: 'right' }}>
        <Button onClick={() => { localClearFilters(); setSelectedKeys([]); confirm(); }} style={{ marginRight: 8 }}>Reset</Button>
        <Button type="primary" onClick={confirm}>Filter</Button>
      </div>
    </div>
  );

  const isInDateRange = (value, dateField) => {
    if (!value || !dateField) return true;
    const [startStr, endStr] = value.split('|');
    const recordDate = dayjs(dateField);
    const startDate = dayjs(startStr);
    const endDate = dayjs(endStr);
    if (!recordDate.isValid() || !startDate.isValid() || !endDate.isValid()) return true;
    return recordDate.isBetween(startDate.startOf('day'), endDate.endOf('day'), null, '[]');
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const BulkActionMenu = ({ selectedItems }) => {
    const canActivate = selectedItems.some(item => item.status === 'Inactive' && !item.isArchived);
    const canDeactivate = selectedItems.some(item => item.status === 'Active');
    const canArchive = selectedItems.some(item => item.status !== 'Active' && !item.isArchived && (item._dataType === 'account' || item.hasOwnProperty('isArchived')));
    const canUnarchive = selectedItems.some(item => item.isArchived && (item._dataType === 'account' || item.hasOwnProperty('isArchived')));

    return (
        <Menu onClick={({ key }) => handleBulkAction(key, selectedItems)}>
            {canActivate && <Menu.Item key="activate" icon={<FaUserCheck />}>Activate Selected</Menu.Item>}
            {canDeactivate && <Menu.Item key="deactivate" icon={<FaUserTimes />} danger>Deactivate Selected</Menu.Item>}
            {canArchive && <Menu.Item key="archive" icon={<FaBoxArchive />} danger>Archive Selected</Menu.Item>}
            {canUnarchive && <Menu.Item key="unarchive" icon={<FaBoxOpen />}>Unarchive Selected</Menu.Item>}
            {( !canActivate && !canDeactivate && !canArchive && !canUnarchive) && (
                <Menu.Item disabled>No applicable actions for selection</Menu.Item>
            )}
        </Menu>
    );
  };


  return (
    <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
      <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
      {showArchived && (
        <Tag className="archived-tag" color="volcano"> Viewing <strong>Archived Records</strong> </Tag>
      )}
      <div className="admin-content-wrapper">
        <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
        <main className="admin-main-content-area">
          <div className="page-title">
            <p className="heading">
              {showArchived ? "Archived Accounts" : "Manage Accounts"}
            </p>
          </div>

          <div className="table-functions">
            <div className="left-tools">
               <Select
                value={selectedYear}
                style={{ width: 150, marginRight: 8 }}
                onChange={(value) => {setSelectedYear(value); setPagination(prev => ({...prev, current:1}));}}
                placeholder="Year (Created)"
                allowClear
                onClear={() => {setSelectedYear(null); setPagination(prev => ({...prev, current:1}));}}
                loading={availableYears.length === 0 && loading}
              >
                <Option value={null}>All Years</Option>
                {availableYears.map(year => (
                  <Option key={year} value={year}> {year} </Option>
                ))}
              </Select>
              <Button icon={<FiFilter />} onClick={handleClearFilters}>Clear Filters</Button>
              <Button icon={<HiOutlineRefresh />} onClick={handleRefresh}>Refresh Data</Button>
              <Button icon={<BiExport />} onClick={handleExport}>Export</Button>
            </div>
            <div className="right-tools">
              <Input
                placeholder="Search by ID, Name, Email, Role" allowClear style={{ width: 300 }}
                value={searchTerm} onChange={(e) => {setSearchTerm(e.target.value); setPagination(prev => ({...prev, current:1}));}}
                suffix={<FaSearch style={{ color: '#aaa' }} />}
              />
              <Button icon={showArchived ? <FaBoxOpen /> : <FaBoxArchive />} onClick={() => {setShowArchived(!showArchived); setPagination(prev => ({...prev, current:1}));}}>
                {showArchived ? 'Close Archive' : 'Show Archive'}
              </Button>
              <Button icon={<MdOutlineManageAccounts />} onClick={handleAccessControl}>Access Control</Button>
              <Button type="primary" className="create-btn" icon={<FaPlus />} onClick={handleCreate}>
                Create Account
              </Button>
            </div>
          </div>
            
            <div style={{ margin: '10px 0', display: 'flex', justifyContent: 'flex-end' }}>
                {selectedRowKeys.length > 0 && (
                <Dropdown 
                    overlay={<BulkActionMenu selectedItems={dataForCurrentTab.filter(item => selectedRowKeys.includes(item.key))} />} 
                    placement="bottomLeft"
                    disabled={loading}
                >
                    <Button style={{ marginLeft: 8 }}>
                    Bulk Actions ({selectedRowKeys.length}) <BsThreeDotsVertical style={{ verticalAlign: 'middle' }}/>
                    </Button>
                </Dropdown>
                )}
            </div>


          <Tabs
            activeKey={activeTabKey}
            onChange={(key) => {
              setActiveTabKey(key);
              setTableFiltersFromAnt({}); setSorterFromAnt({}); setSelectedRowKeys([]);
              setPagination(prev => ({ ...prev, current: 1 })); 
            }}
            type="card" style={{ marginTop: '5px' }}
          >
            {tabItems.map(tab => (
              <Tabs.TabPane tab={tab.label} key={tab.key}>
                {activeTabKey === tab.key && (
                  <Table
                    className="accounts-table"
                    rowSelection={rowSelection} 
                    style={{ width: '100%', flex: 1 }}
                    columns={currentColumns}
                    dataSource={processedTableData} 
                    loading={loading}
                    scroll={{ x: 1500 }}
                    bordered
                    pagination={{ ...pagination }}
                    onChange={(newPagination, filters, sorterConfig) => {
                        setPagination(prev => ({ ...prev, current: newPagination.current, pageSize: newPagination.pageSize }));
                        setTableFiltersFromAnt(filters);
                        setSorterFromAnt(sorterConfig);
                    }}
                  />
                )}
              </Tabs.TabPane>
            ))}
          </Tabs>
        </main>
      </div>
      <Footer />

        {isBulkActionModalVisible && (
          <Modal
            title={`Confirm Bulk ${currentBulkAction.charAt(0).toUpperCase() + currentBulkAction.slice(1)}`}
            visible={isBulkActionModalVisible}
            onOk={confirmBulkAction}
            onCancel={() => setIsBulkActionModalVisible(false)}
            width={700}
            okText={<><MdDoneAll style={{marginRight: '5px'}}/>Confirm {currentBulkAction.charAt(0).toUpperCase() + currentBulkAction.slice(1)}</>}
            cancelText={<><MdCancel style={{marginRight: '5px'}}/>Cancel</>}
            confirmLoading={loading}
          >
            <p>You are about to <strong>{currentBulkAction}</strong> the following records. Uncheck any items to exclude them from this action.</p>
            <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #f0f0f0', padding: '8px', marginBottom: '15px' }}>
              {itemsForBulkAction.map(item => (
                <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', padding: '5px 8px', borderBottom: '1px dashed #eee', borderRadius: '4px', background: !exceptions.has(item._id) ? '#f9f9f9' : '#fff' }}>
                  <Checkbox
                    checked={!exceptions.has(item._id)}
                    onChange={(e) => {
                      const newExceptions = new Set(exceptions);
                      if (e.target.checked) newExceptions.delete(item._id);
                      else newExceptions.add(item._id);
                      setExceptions(newExceptions);
                    }}
                  >
                    <strong>{item.name}</strong> ({item.displayID})
                  </Checkbox>
                  <Tag color={item._dataType === 'account' ? 'blue' : 'purple'}>{item.roleForDisplay}</Tag>
                </div>
              ))}
            </div>
            <p style={{marginTop: '10px', fontWeight: 500}}>
                Total eligible initially: {itemsForBulkAction.length}. <br/>
                Items to be processed: {itemsForBulkAction.filter(item => !exceptions.has(item._id)).length}.
            </p>
          </Modal>
        )}

    </div>
  );
};

export default ManageAccountsPage;